/**
 * Operator conversation takeover in the Escalations inbox (agion/Agion#3435),
 * shared by the current and the new chat: Take over accepts a hold and opens
 * the run's conversation in operator mode, and the holds the viewer holds
 * are read on the inbox's poll so each offers Open conversation. Governance
 * decides who may take over; a refusal stays in the escalation store.
 */

import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from './useAuth'
import { useEscalationStore } from './useEscalations'
import { operatorConversationHref } from '../services/operator-takeover'
import { operatorTakeoverEnabled } from '../utils/config'
import type { Escalation } from '../types/escalations'

const POLL_INTERVAL_MS = 30_000

/**
 * Takes over the hold and, once governance accepted it, opens its run in
 * operator mode; the conversation opens operator mode only after it reads the
 * hold as the viewer's.
 */
export function useTakeOver(): (hold: Escalation) => Promise<void> {
  const navigate = useNavigate()
  const userId = useAuth((s) => s.user?.id)
  const acceptHandoff = useEscalationStore((s) => s.acceptHandoff)
  return useCallback(async (hold: Escalation) => {
    if (!hold.mission_id || !hold.execution_id || !userId) return
    if (await acceptHandoff(hold.id, userId)) {
      navigate(operatorConversationHref(hold.mission_id, hold.execution_id, hold.id))
    }
  }, [acceptHandoff, navigate, userId])
}

/**
 * Whether governance names the viewer as the run's starter (subject_user_id),
 * who cannot take over their own conversation: the inbox then says so instead
 * of offering Take over. Governance refuses the accept either way.
 */
export function useStartedByViewer(hold: Escalation): boolean {
  const userId = useAuth((s) => s.user?.id)
  return Boolean(userId) && hold.subject_user_id === userId
}

/**
 * The accepted holds whose operator is the viewer, read while mounted, every
 * 30 s and when the tab becomes visible again. Empty with takeover off.
 */
export function useHeldConversations(): Escalation[] {
  const enabled = operatorTakeoverEnabled()
  const userId = useAuth((s) => s.user?.id)
  const holds = useEscalationStore((s) => s.operatorHolds)
  const loadOperatorHolds = useEscalationStore((s) => s.loadOperatorHolds)

  useEffect(() => {
    if (!enabled || !userId) return
    const load = () => void loadOperatorHolds(userId)
    load()
    const interval = setInterval(load, POLL_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled, userId, loadOperatorHolds])

  return enabled ? holds ?? [] : []
}
